import { FC, FocusEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContentCopy } from '@mui/icons-material'
import { Box, Button, Chip, Paper, Popper, Skeleton, Stack, Typography } from '@mui/material'

import { AnalyzedVersionPerMarket } from '../../types/GameAnalyses'

type AnalyzedVersionsPerMarketProps = {
  newestVersion?: string
  perMarket: AnalyzedVersionPerMarket[]
  isLoading: boolean
  // Without a clone source no badge can clone.
  canClone: boolean
  onClone: (entry: AnalyzedVersionPerMarket) => void
}

export const AnalyzedVersionsPerMarket: FC<AnalyzedVersionsPerMarketProps> = ({ newestVersion, perMarket, isLoading, canClone, onClone }) => {
  const { t } = useTranslation()

  if (isLoading) return <Skeleton variant="rectangular" height={64} />
  if (!newestVersion || !perMarket.length) return null

  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight="bold">
        {t('internal-analysts:games_analyses_per_market', { version: newestVersion })}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {perMarket.map((entry) => (
          <MarketVersionBadge key={entry.marketIso} entry={entry} isNewest={entry.gameVersion === newestVersion} canClone={canClone} onClone={onClone} />
        ))}
      </Stack>
    </Stack>
  )
}

type MarketVersionBadgeProps = {
  entry: AnalyzedVersionPerMarket
  isNewest: boolean
  canClone: boolean
  onClone: (entry: AnalyzedVersionPerMarket) => void
}

const MarketVersionBadge: FC<MarketVersionBadgeProps> = ({ entry, isNewest, canClone, onClone }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsOpen(false)
  }

  return (
    <Box
      ref={setAnchorEl}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={handleBlur}
      data-testid={`per-market-${entry.marketIso}`}
      sx={{ display: 'flex' }}
    >
      <Chip
        size="small"
        tabIndex={0}
        color={isNewest ? 'success' : 'warning'}
        label={t('internal-analysts:games_analyses_per_market_badge', { market: entry.marketName, version: entry.gameVersion || '-' })}
      />
      {/* Kept inside the badge so moving the pointer onto the button keeps it open, and Tab reaches it right after the chip.
          Opening below the badge keeps the neighbouring badges of its row uncovered. */}
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        disablePortal
        modifiers={[{ name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] } }]}
        sx={{ zIndex: 1 }}
      >
        <Paper elevation={3}>
          <Button
            size="small"
            startIcon={<ContentCopy fontSize="small" />}
            disabled={!canClone}
            onClick={() => onClone(entry)}
            aria-label={`${t('internal-analysts:games_analyses_clone_latest')} ${entry.marketName}`}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('internal-analysts:games_analyses_clone_latest')}
          </Button>
        </Paper>
      </Popper>
    </Box>
  )
}
